import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import { fetchJson } from "../lib/api";
import { ReorderInput } from "@shukan/shared";
import type { ApiHabitRow } from "../types/habit";
import type { UserStats } from "@shukan/shared";
import type { JournalEntry, QuickThought } from "@shukan/shared";

type ReorderInputT = z.infer<typeof ReorderInput>;

export function useHabits() {
  return useQuery({
    queryKey: ["habits"],
    queryFn: () => fetchJson<ApiHabitRow[]>("/api/habits"),
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ["user-stats"],
    queryFn: () => fetchJson<UserStats>("/api/user/stats"),
    staleTime: 60_000,
  });
}

export function useTodayCompletions(date: string) {
  return useQuery({
    queryKey: ["completions", date],
    queryFn: async () => {
      const all = await fetchJson<Array<{ habit_id: string; date: string; value: number }>>(
        `/api/completions?from=${date}&to=${date}`,
      );
      return all;
    },
  });
}

export function useToggleCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { habitId: string; date: string; value?: number; notes?: string; customXp?: number }) => {
      return fetchJson<unknown>("/api/completions", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onMutate: async (variables) => {
      const { habitId, date, value, customXp } = variables;
      const targetValue = value ?? 1;

      // Cancel outgoing queries
      await qc.cancelQueries({ queryKey: ["completions"] });
      await qc.cancelQueries({ queryKey: ["user-stats"] });

      // Snapshot the previous values
      const previousCompletions = qc.getQueryData<any[]>(["completions", date]);
      const previousRangeCompletions = qc.getQueryCache()
        .findAll({ queryKey: ["completions"] })
        .map(q => ({ key: q.queryKey, data: q.state.data as any[] }))
        .filter((item) => item.key && item.key.includes("range"));

      const previousUserStats = qc.getQueryData<any>(["user-stats"]);

      // Get habit details
      const habits = qc.getQueryData<ApiHabitRow[]>(["habits"]) || [];
      const habit = habits.find((h) => h.id === habitId);

      if (habit) {
        // 1. Update completions for the specific date
        qc.setQueryData<any[]>(["completions", date], (old) => {
          const list = old ? [...old] : [];
          const idx = list.findIndex(c => c.habit_id === habitId);
          if (idx >= 0) {
            list[idx] = { ...list[idx], value: targetValue };
          } else {
            list.push({ habit_id: habitId, date, value: targetValue });
          }
          return list;
        });

        // 2. Update completions for any range queries containing the date
        for (const { key, data } of previousRangeCompletions) {
          const from = key[2];
          const to = key[3];
          if (typeof from === "string" && typeof to === "string" && date >= from && date <= to) {
            qc.setQueryData<any[]>(key as any[], (old) => {
              const list = old ? [...old] : [];
              const idx = list.findIndex(c => c.habit_id === habitId && c.date === date);
              if (idx >= 0) {
                list[idx] = { ...list[idx], value: targetValue };
              } else {
                list.push({ habit_id: habitId, date, value: targetValue });
              }
              return list;
            });
          }
        }

        // 3. Update user-stats (XP and Level)
        if (previousUserStats) {
          const existing = previousCompletions?.find(c => c.habit_id === habitId);
          const wasCompleted = existing ? (habit.type === "boolean" || existing.value >= habit.target_value) : false;
          const isCompleted = habit.type === "boolean" || targetValue >= habit.target_value;

          let xpDelta = 0;
          const xpVal = customXp ?? habit.xp_value;
          if (!wasCompleted && isCompleted) {
            xpDelta = xpVal;
          } else if (wasCompleted && !isCompleted) {
            xpDelta = -xpVal;
          }

          if (xpDelta !== 0) {
            const newTotalXp = Math.max(0, previousUserStats.totalXp + xpDelta);
            const newLevel = Math.max(0, Math.floor(Math.sqrt(newTotalXp / 50)));
            qc.setQueryData(["user-stats"], {
              ...previousUserStats,
              totalXp: newTotalXp,
              level: newLevel,
            });
          }
        }
      }

      return { previousCompletions, previousRangeCompletions, previousUserStats };
    },
    onError: (_err, variables, context: any) => {
      const { date } = variables;
      if (context) {
        if (context.previousCompletions !== undefined) {
          qc.setQueryData(["completions", date], context.previousCompletions);
        }
        if (context.previousRangeCompletions) {
          for (const { key, data } of context.previousRangeCompletions) {
            qc.setQueryData(key, data);
          }
        }
        if (context.previousUserStats !== undefined) {
          qc.setQueryData(["user-stats"], context.previousUserStats);
        }
      }
    },
    onSettled: (_data, _error, variables) => {
      const { date } = variables;
      qc.invalidateQueries({ queryKey: ["completions", date] });
      qc.invalidateQueries({ queryKey: ["completions", "range"] });
      qc.invalidateQueries({ queryKey: ["habits"] });
      qc.invalidateQueries({ queryKey: ["user-stats"] });
      qc.invalidateQueries({ queryKey: ["heatmap"] });
    },
  });
}

export function useDeleteCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { habitId: string; date: string }) => {
      return fetchJson<unknown>("/api/completions", {
        method: "DELETE",
        body: JSON.stringify(input),
      });
    },
    onMutate: async (variables) => {
      const { habitId, date } = variables;

      // Cancel outgoing queries
      await qc.cancelQueries({ queryKey: ["completions"] });
      await qc.cancelQueries({ queryKey: ["user-stats"] });

      // Snapshot the previous values
      const previousCompletions = qc.getQueryData<any[]>(["completions", date]);
      const previousRangeCompletions = qc.getQueryCache()
        .findAll({ queryKey: ["completions"] })
        .map(q => ({ key: q.queryKey, data: q.state.data as any[] }))
        .filter((item) => item.key && item.key.includes("range"));

      const previousUserStats = qc.getQueryData<any>(["user-stats"]);

      // Get habit details
      const habits = qc.getQueryData<ApiHabitRow[]>(["habits"]) || [];
      const habit = habits.find((h) => h.id === habitId);

      if (habit) {
        // 1. Update completions for the specific date
        qc.setQueryData<any[]>(["completions", date], (old) => {
          if (!old) return [];
          return old.filter(c => c.habit_id !== habitId);
        });

        // 2. Update completions for any range queries containing the date
        for (const { key, data } of previousRangeCompletions) {
          const from = key[2];
          const to = key[3];
          if (typeof from === "string" && typeof to === "string" && date >= from && date <= to) {
            qc.setQueryData<any[]>(key as any[], (old) => {
              if (!old) return [];
              return old.filter(c => !(c.habit_id === habitId && c.date === date));
            });
          }
        }

        // 3. Update user-stats (XP and Level)
        if (previousUserStats) {
          const existing = previousCompletions?.find(c => c.habit_id === habitId);
          const wasCompleted = existing ? (habit.type === "boolean" || existing.value >= habit.target_value) : false;

          if (wasCompleted) {
            const newTotalXp = Math.max(0, previousUserStats.totalXp - habit.xp_value);
            const newLevel = Math.max(0, Math.floor(Math.sqrt(newTotalXp / 50)));
            qc.setQueryData(["user-stats"], {
              ...previousUserStats,
              totalXp: newTotalXp,
              level: newLevel,
            });
          }
        }
      }

      return { previousCompletions, previousRangeCompletions, previousUserStats };
    },
    onError: (_err, variables, context: any) => {
      const { date } = variables;
      if (context) {
        if (context.previousCompletions !== undefined) {
          qc.setQueryData(["completions", date], context.previousCompletions);
        }
        if (context.previousRangeCompletions) {
          for (const { key, data } of context.previousRangeCompletions) {
            qc.setQueryData(key, data);
          }
        }
        if (context.previousUserStats !== undefined) {
          qc.setQueryData(["user-stats"], context.previousUserStats);
        }
      }
    },
    onSettled: (_data, _error, variables) => {
      const { date } = variables;
      qc.invalidateQueries({ queryKey: ["completions", date] });
      qc.invalidateQueries({ queryKey: ["completions", "range"] });
      qc.invalidateQueries({ queryKey: ["habits"] });
      qc.invalidateQueries({ queryKey: ["user-stats"] });
      qc.invalidateQueries({ queryKey: ["heatmap"] });
    },
  });
}

export function useApplyFreeze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { habitId: string; date: string }) => {
      return fetchJson<unknown>("/api/completions/freeze", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
    },
  });
}

export function useJournalByDate(date: string) {
  return useQuery({
    queryKey: ["journal", date],
    queryFn: () => fetchJson<JournalEntry | null>(`/api/journal/date/${date}`),
  });
}

export function useSaveJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { date: string; content: string; title?: string; moodScore?: number }) => {
      return fetchJson<JournalEntry>("/api/journal", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["journal", vars.date] });
    },
  });
}

export function useQuickThoughts() {
  return useQuery({
    queryKey: ["thoughts"],
    queryFn: () => fetchJson<QuickThought[]>("/api/thoughts"),
    refetchInterval: 30_000,
  });
}

export function useAddThought() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { content: string; deferTo?: "today" | "tomorrow" | "evening" | "weekly" }) => {
      return fetchJson<QuickThought>("/api/thoughts", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thoughts"] });
    },
  });
}

export function useDeleteThought() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return fetchJson<{ ok: boolean }>(`/api/thoughts/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thoughts"] });
    },
  });
}

export function useReorderHabits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderInputT) => {
      const parsed = ReorderInput.parse(input);
      return fetchJson<{ ok: true }>("/api/habits/reorder", {
        method: "PATCH",
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
    },
  });
}

export function useReorderGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderInputT) => {
      const parsed = ReorderInput.parse(input);
      return fetchJson<{ ok: true }>("/api/habits/groups/reorder", {
        method: "PATCH",
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
    },
  });
}

export function useCreateHabitGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; icon?: string; color?: string }) => {
      return fetchJson<any>("/api/habits/groups", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
    },
  });
}

export function useCreateHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      groupId: string;
      name: string;
      type: "boolean" | "counter" | "timer";
      icon?: string;
      targetValue?: number;
      xpValue?: number;
      isOneOff?: boolean;
      targetDate?: string | null;
      timeOfDay?: string;
    }) => {
      return fetchJson<any>("/api/habits", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
    },
  });
}

export function useDeleteHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return fetchJson<{ ok: boolean }>(`/api/habits/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
      qc.invalidateQueries({ queryKey: ["completions"] });
      qc.invalidateQueries({ queryKey: ["user-stats"] });
      qc.invalidateQueries({ queryKey: ["heatmap"] });
    },
  });
}

export function useCompletionsRange(from: string, to: string) {
  return useQuery({
    queryKey: ["completions", "range", from, to],
    queryFn: async () => {
      const all = await fetchJson<Array<{ habit_id: string; date: string; value: number }>>(
        `/api/completions?from=${from}&to=${to}`,
      );
      return all;
    },
  });
}

export function useDailyPlan(date: string) {
  return useQuery({
    queryKey: ["daily-plan", date],
    queryFn: () => fetchJson<{
      id: string;
      date: string;
      habits_json: string[];
      notes: string | null;
    } | null>(`/api/plans/today?date=${date}`),
    staleTime: 30_000,
  });
}

export function useGeneratePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (date: string) => {
      return fetchJson<any>("/api/plans/generate", {
        method: "POST",
        body: JSON.stringify({ date }),
      });
    },
    onSuccess: (data) => {
      if (data?.date) {
        qc.invalidateQueries({ queryKey: ["daily-plan", data.date] });
        qc.invalidateQueries({ queryKey: ["habits"] });
        qc.invalidateQueries({ queryKey: ["completions"] });
      }
    },
  });
}

